import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { VonPageResponseModel } from '@von-development-studio/angular-rest-service';
import { VonConfirmationDialogService } from '@von-development-studio/primeng-confirmation-dialog';
import { VonMessageService } from '@von-development-studio/primeng-message-service';
import { FilterMetadata, SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { RestService } from 'src/app/core/service/rest.service';
import { AdminCatalogModel } from 'src/app/model/admin/catalog';
import { CommonDoctorModel } from 'src/app/model/common/doctor';
import { CommonDoctorViewModel } from 'src/app/model/common/doctor.view';
import { KhuyanaSelectItemModel } from 'src/app/model/core/khuyana-select-item.model';
import { commonPersonActions } from 'src/app/store/common/person/person.action';
import { environment } from 'src/environments/environment';
import { commonDoctorActions } from '../../../store/common/doctor/doctor.action';
import { AdminLocationService } from '../../admin/location/admin-location.service';
import { AdminSubCatalogService } from '../../admin/subcatalog/admin-subcatalog.service';
import { PersonService } from '../person/person.service';
import { DoctorSpecialtiesDialogComponent } from './components/doctor-specialties-dialog.component';
import { DOCTOR } from './doctor.constants';

@Injectable({
  providedIn: 'root',
})
export class DoctorService extends BaseService {
  readonly screen = DOCTOR;

  constructor(
    protected store: Store<any>,
    protected rest: RestService,
    protected messageService: VonMessageService,
    protected dialogService: DialogService,
    protected personService: PersonService,
    protected confirmationDialogService: VonConfirmationDialogService,
    protected subCatalogService: AdminSubCatalogService,
    protected locationService: AdminLocationService
  ) {
    super();
  }

  readStore = () => {
    const locations: Observable<SelectItem<KhuyanaSelectItemModel>[]> =
      this.store.select('admin', 'location', 'queryResult');
    const doctorData: Observable<CommonDoctorModel> = this.store.select(
      'common',
      'doctor',
      'data'
    );
    const residence: Observable<SelectItem<KhuyanaSelectItemModel>> =
      this.store.select('common', 'doctor', 'residence');
    return { locations, doctorData, residence };
  };

  readSubCatalogStore = (code: string): Observable<AdminCatalogModel[]> =>
    this.subCatalogService.getSubCatalogStore(code);

  readLocations = (query: string) =>
    this.locationService.readLocationSelectors(query);

  readDoctorLazyLoad = (): Observable<
    VonPageResponseModel<CommonDoctorViewModel>
  > => this.store.select('common', 'doctor', 'result');

  getPersonById = this.personService.getPersonById;

  getDoctorByPerson = (person: number): Observable<CommonDoctorModel> =>
    this.rest
      .get<CommonDoctorModel>({
        url: `${environment.endpoints.common}/doctor/by/${person}`,
      })
      .pipe(tap(this.doctorSuccess, this.doctorFailure));

  getDoctorById = (id: number): Observable<CommonDoctorModel> =>
    this.rest
      .get<CommonDoctorModel>({
        url: `${environment.endpoints.common}/doctor/${id}`,
      })
      .pipe(tap(this.doctorSuccess, this.doctorFailure));

  getDoctors = (
    page: number,
    size: number,
    nameQuery?: FilterMetadata,
    documentQuery?: FilterMetadata
  ): Observable<VonPageResponseModel<CommonDoctorViewModel>> => {
    const body: any = { page, size };
    if (nameQuery) {
      body.nameQuery = nameQuery.value;
    }
    if (documentQuery) {
      body.documentQuery = documentQuery.value;
    }

    return this.rest
      .get<VonPageResponseModel<CommonDoctorViewModel>>({
        url: `${environment.endpoints.common}/doctor`,
        params: body,
      })
      .pipe(
        tap((result) =>
          this.store.dispatch(commonDoctorActions.setResultAction(result))
        )
      );
  };

  doctorSuccess = (doctor: CommonDoctorModel) => {
    let doctorState: CommonDoctorModel;
    if (doctor) {
      doctorState = {
        ...doctor,
        isNew: false,
      };
    } else {
      doctorState = {
        isNew: true,
      };
    }
    this.store.dispatch(commonDoctorActions.setDoctorAction(doctorState));
    if (doctorState.residence) {
      this.rest
        .get<KhuyanaSelectItemModel>({
          url: `${environment.endpoints.admin}/location/selector/${doctorState.residence}`,
        })
        .subscribe((location) =>
          this.store.dispatch(
            commonDoctorActions.setResidenceAction({
              label: location.name,
              value: location,
            })
          )
        );
    } else {
      this.store.dispatch(
        commonDoctorActions.setResidenceAction({ value: {} })
      );
    }
  };

  doctorFailure = () =>
    this.store.dispatch(
      commonDoctorActions.setDoctorAction({
        isNew: true,
        agendaColor: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
      })
    );

  setDoctorInformation = (doctor: CommonDoctorModel): CommonDoctorModel => {
    if (doctor.isNew) {
      return {
        ...doctor,
        personHelp: {},
      };
    }

    return {
      ...doctor,
      creationDate: doctor.creationDate
        ? new Date(doctor.creationDate)
        : undefined,
      updateDate: doctor.updateDate ? new Date(doctor.updateDate) : undefined,
      quitclaimDate: doctor.quitclaimDate
        ? new Date(`${doctor.quitclaimDate}T00:00`)
        : undefined,
      reEntryDate: doctor.reEntryDate
        ? new Date(`${doctor.reEntryDate}T00:00`)
        : undefined,
    };
  };

  save = (doctor: CommonDoctorModel): Observable<CommonDoctorViewModel> =>
    this.rest
      .post<CommonDoctorViewModel>({
        url: `${environment.endpoints.common}/doctor`,
        body: doctor,
      })
      .pipe(
        tap(() =>
          this.messageService.addSuccess(this.screen.labels.saveSuccess)
        )
      );

  generatePass = (username: string, fullName: string) =>
    this.confirmationDialogService.open<any>({
      localization: 'es',
      custom: {
        message: this.screen.labels.generatePassQuestion.replace(
          '{{fullName}}',
          fullName
        ),
        save: this.screen.labels.yes,
        cancel: this.screen.labels.no,
        success: this.screen.labels.saveSuccess,
      },
      save: this.rest.post<any>({
        url: `${environment.endpoints.admin}/user/pass/${username}`,
      }),
    });

  changeStatus = (id: number, status: boolean, fullName: string) =>
    this.confirmationDialogService.open<CommonDoctorViewModel>({
      localization: 'es',
      custom: {
        message: this.screen.labels.statusQuestion
          .replace('{{status}}', status ? 'ACTIVAR' : 'DESACTIVAR')
          .replace('{{fullName}}', fullName),
        save: this.screen.labels.yes,
        cancel: this.screen.labels.no,
        success: this.screen.labels.saveSuccess,
      },
      save: this.rest
        .patch<CommonDoctorViewModel>({
          url: `${environment.endpoints.common}/doctor/{id}/status`,
          urlParams: { id },
        })
        .pipe(
          tap((doctor) => {
            this.store.dispatch(
              commonDoctorActions.patchResultAction({
                ...doctor,
                status: !doctor.status,
                quitclaimDate: doctor.status ? new Date() : undefined,
              })
            );
          })
        ),
    });

  setRole = (role: string, roleLabel: string, doctor: CommonDoctorViewModel) =>
    this.confirmationDialogService.open<CommonDoctorViewModel>({
      localization: 'es',
      custom: {
        message: this.screen.labels.roleQuestion
          .replace('{{role}}', roleLabel)
          .replace('{{fullName}}', doctor.fullName!),
        save: this.screen.labels.yes,
        cancel: this.screen.labels.no,
        success: this.screen.labels.saveSuccess,
      },
      save: this.rest.patch<any>({
        url: `${environment.endpoints.admin}/user/role/{role}/{username}`,
        urlParams: { role, username: doctor.username },
      }),
    });

  addSpecialties = (id: number, specialties: number[]) =>
    this.rest
      .put<CommonDoctorViewModel>({
        url: `${environment.endpoints.common}/doctor/{id}/specialties`,
        body: specialties,
        urlParams: { id },
      })
      .pipe(
        tap(() => {
          this.messageService.addSuccess(this.screen.labels.saveSuccess);
        })
      );

  openDoctorDialog = (isNew: boolean = true, component: any) => {
    if (isNew) {
      this.personService.setPerson();
      this.doctorFailure();
    }
    const dialogRef = this.dialogService.open(component, {
      data: {
        save: this.save,
      },
      header: isNew ? this.screen.actions.add : this.screen.actions.edit,
      closeOnEscape: true,
      width: '75%',
    });
    dialogRef.onClose.subscribe((modalResult) => {
      if (modalResult) {
        const action = modalResult.isNew
          ? commonDoctorActions.addResultAction
          : commonDoctorActions.patchResultAction;
        this.store.dispatch(action(modalResult));
      }
      this.store.dispatch(commonDoctorActions.setDoctorDefaultAction(true));
      this.store.dispatch(commonPersonActions.setPersonDefaultAction(true));
    });
  };

  openManageSpecialtiesDialog = (doctor: CommonDoctorViewModel) => {
    const dialogRef = this.dialogService.open(
      DoctorSpecialtiesDialogComponent,
      {
        data: {
          doctor,
          save: this.addSpecialties,
        },
        header: doctor.fullName!,
        style: {
          maxHeight: '60%',
        },
        closeOnEscape: true,
        width: '60%',
      }
    );
    dialogRef.onClose.subscribe((modalResult) => {
      if (modalResult) {
        this.store.dispatch(commonDoctorActions.patchResultAction(modalResult));
      }
      this.store.dispatch(commonDoctorActions.setDoctorDefaultAction(true));
      this.store.dispatch(commonPersonActions.setPersonDefaultAction(true));
    });
  };
}
